import React from "react";
import { Link, useOutletContext } from "react-router-dom";
import "./style.css";

import { Box, Card, Typography, Tooltip } from "@mui/material";
import { Line } from "react-chartjs-2";
import {
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  LineElement,
  PointElement,
} from "chart.js";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import moment from "moment-timezone";
import "moment/locale/ru";
import "moment/locale/de";
import "moment/locale/fr";
import "moment/locale/it";
import i18n from "i18next";

import _request from "../../actions/_request";
import PageAbstract from "../../components/PageAbstract";
import IconsAccounts from "../../components/IconsAccounts";
import AccountAnalytics from "../../components/AccountAnalytics";
import AvatarIcon from "../../components/AvatarIcon";
import ImageError from "../../components/ImageError";
import { resolvePostPermalink } from "../../utils/postPermalink";
import { ReactComponent as NoPost } from "../../images/No posts.svg";
import { ReactComponent as NoData } from "../../images/No upcoming posts.svg";

ChartJS.register(CategoryScale, LineElement, LinearScale, PointElement);

// ─── Pure helpers ────────────────────────────────────────────────────────────

function getDateRange(start, end) {
  const date = new Date(start);
  const dates = [];
  while (date <= end) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    dates.push(`${y}-${m}-${d}`);
    date.setDate(date.getDate() + 1);
  }
  return dates;
}

function formatDate(dateString) {
  const date = new Date(dateString);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function filterByRange(tasks, range) {
  const now = new Date();
  const days = { "3Days": 3, week: 7, month: 30 }[range];
  if (!days) return tasks;
  const start = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  return tasks.filter((t) => new Date(t.created_at) >= start);
}

function getRangeStart(range, tasks) {
  const now = new Date();
  const days = { "3Days": 3, week: 7, month: 30 }[range];
  if (days) return new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  return tasks.reduce((min, t) => {
    const d = new Date(t.created_at);
    return d < min ? d : min;
  }, now);
}

function getAxisTickColor() {
  if (typeof window === "undefined") return "rgba(255,255,255,0.75)";
  const val = getComputedStyle(document.body).getPropertyValue("--text-color").trim();
  return val || "rgba(255,255,255,0.75)";
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function PostCard({ post, accounts, metaData, tr }) {
  const { social_type, account_id, type_data = {}, schedule_utc_date, account } = post;
  const found = accounts?.find((a) => a.id === account_id) || {};
  const username = found.username || account?.username || tr("dashboardPage.unknownUser");
  const avatar =
    found.avatar ||
    account?.avatar ||
    "https://cdn-icons-png.flaticon.com/512/149/149071.png";
  const tz = metaData?.timezone || moment.tz.guess();
  const timeLabel = moment.utc(schedule_utc_date).tz(tz).fromNow();
  const previewSrc =
    type_data.files &&
    (Array.isArray(type_data.files) ? type_data.files.length > 0 : type_data.files)
      ? _request.files.formLink(type_data.files)
      : null;

  const permalink = resolvePostPermalink(post);

  const card = (
    <Card className="db-post-card">
      <Box className="db-post-avatar">
        <AvatarIcon avatar={avatar} width="38px" height="38px" />
      </Box>
      <Box className="db-post-body">
        <Box className="db-post-title-row">
          <Typography className="db-post-username">{username}</Typography>
          <IconsAccounts socialType={social_type} width="15px" height="15px" />
          {post.status === "fail" && (
            <span className="db-fail-badge">{tr("dashboardPage.fail")}</span>
          )}
          {permalink && (
            <OpenInNewIcon
              sx={{ fontSize: "13px", color: "var(--accent-secondary)", opacity: 0.8 }}
            />
          )}
        </Box>
        <Typography className="db-post-text">{type_data.text || ""}</Typography>
      </Box>
      <Box className="db-post-meta">
        <Typography className="db-post-time">{timeLabel}</Typography>
        {previewSrc && (
          <ImageError src={previewSrc} className="db-post-thumb" width="52px" height="52px" />
        )}
      </Box>
    </Card>
  );

  // A published post is worth opening; a failed one has nothing to open, and a
  // provider that gave us no usable id is left as plain text rather than a
  // broken link.
  if (!permalink) return card;

  return (
    <Tooltip title={tr("dashboardPage.openPost")} arrow>
      <a
        href={permalink}
        target="_blank"
        rel="noopener noreferrer"
        className="db-post-link">
        {card}
      </a>
    </Tooltip>
  );
}

function UpcomingCard({ post, posts, accounts, metaData }) {
  const { social_type, account_id, type_data = {}, schedule_utc_date } = post;
  const found = accounts?.find((a) => a.id === account_id);
  const username = found?.username || "Unknown";
  const avatar =
    found?.avatar || "https://cdn-icons-png.flaticon.com/512/149/149071.png";
  const parentPost = posts?.find((p) => p.id === post.parent_post_id);
  const repeat = parentPost?.repeat_freq;
  const tz = metaData?.timezone || moment.tz.guess();
  const timeLabel = moment.utc(schedule_utc_date).tz(tz).format("DD MMM, HH:mm");
  const previewSrc =
    type_data.files &&
    (Array.isArray(type_data.files) ? type_data.files.length > 0 : type_data.files)
      ? _request.files.formLink(type_data.files)
      : null;

  return (
    <Card className="db-post-card">
      <Box className="db-post-avatar">
        <AvatarIcon avatar={avatar} width="38px" height="38px" />
      </Box>
      <Box className="db-post-body">
        <Box className="db-post-title-row">
          <Typography className="db-post-username">{username}</Typography>
          <IconsAccounts socialType={social_type} width="15px" height="15px" />
          {repeat && repeat !== "no_repeat" && (
            <EventRepeatIcon
              sx={{ color: "var(--accent-secondary)", fontSize: "14px" }}
            />
          )}
        </Box>
        <Typography className="db-post-text">{type_data.text || ""}</Typography>
      </Box>
      <Box className="db-post-meta">
        <Typography className="db-post-time">{timeLabel}</Typography>
        {previewSrc && (
          <ImageError src={previewSrc} className="db-post-thumb" width="52px" height="52px" />
        )}
      </Box>
    </Card>
  );
}

function MiniStatCard({ label, value, chartData, chartOptions, trend, trendColor }) {
  return (
    <Box className="db-mini-card">
      <Typography className="db-mini-label">{label}</Typography>
      <Typography className="db-mini-value">{value}</Typography>
      <Box className="db-mini-chart">
        <Line data={chartData} options={chartOptions} />
      </Box>
      <Typography className="db-mini-trend" style={{ color: trendColor }}>
        {trend}
      </Typography>
    </Box>
  );
}

// ─── Page class ──────────────────────────────────────────────────────────────

class DashboardPage extends PageAbstract {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      postTasks: [],
      posts: [],
      accounts: [],
      files: [],
      timeRange: "week",
      isLoading: true,
      metaData: {},
      currentLang: i18n?.language || "en",
    };
    this._isMounted = false;
  }

  async loadPosts() {
    const { companyType } = this.props;
    this.setState({ isLoading: true });
    try {
      await _request.accounts.loading(async () => {
        const [resp, respPosts, respAccounts, respFile] = await Promise.all([
          _request.postTasks.getAll([], [], companyType),
          _request.posts.getAll(),
          _request.accounts.getAll(),
          _request.files.getAll(),
        ]);
        if (!this._isMounted) return;
        this.setState({
          postTasks: resp?.data?.items || [],
          posts: respPosts?.data?.items || [],
          accounts: respAccounts?.data?.items || [],
          files: respFile?.data?.items || [],
        });
      });
      const metaResp = await _request.users.getMeta();
      if (!this._isMounted) return;
      this.setState({ metaData: metaResp?.data?.user || {} });
    } finally {
      if (this._isMounted) this.setState({ isLoading: false });
    }
  }

  componentDidMount() {
    this._isMounted = true;
    this.loadPosts();
    this.syncMomentLocale();
    if (i18n?.on) {
      this._i18nHandler = (lng) => {
        this.setState({ currentLang: lng }, () => this.syncMomentLocale());
      };
      i18n.on("languageChanged", this._i18nHandler);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.companyType !== this.props.companyType) this.loadPosts();
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (i18n?.off && this._i18nHandler)
      i18n.off("languageChanged", this._i18nHandler);
  }

  syncMomentLocale() {
    const map = { en: "en", ru: "ru", de: "de", fr: "fr", it: "it" };
    moment.locale(map[i18n.language] || "en");
  }

  tr(key, options = {}) {
    this.syncMomentLocale();
    return i18n.t(key, options);
  }

  // ── Schedule summary chart ────────────────────────────────────────────────

  renderScheduleChart() {
    const { timeRange, postTasks } = this.state;
    const now = new Date();
    const startDate = getRangeStart(timeRange, postTasks);
    const filtered = filterByRange(postTasks, timeRange);
    const labels = getDateRange(startDate, now);

    const counts = Object.fromEntries(
      labels.map((d) => [d, { completed: 0, failed: 0 }])
    );
    filtered.forEach((t) => {
      const d = formatDate(t.created_at);
      if (counts[d])
        counts[d][t.status === "fail" ? "failed" : "completed"] += 1;
    });

    const tickColor = getAxisTickColor();
    const maxTicks = timeRange === "month" ? 10 : 7;

    const data = {
      labels: labels.map((d) => moment(d).format("DD MMM")),
      datasets: [
        {
          label: this.tr("dashboardPage.completedPosts"),
          data: labels.map((d) => counts[d].completed),
          borderColor: "#00E5FF",
          backgroundColor: "transparent",
          borderWidth: 2.5,
          pointRadius: 0,
          tension: 0.45,
        },
        {
          label: this.tr("dashboardPage.failedPosts"),
          data: labels.map((d) => counts[d].failed),
          borderColor: "#B6B4FF",
          backgroundColor: "transparent",
          borderWidth: 2.5,
          pointRadius: 0,
          tension: 0.45,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 2,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(0,0,0,0.8)",
          titleColor: "#fff",
          bodyColor: "#fff",
          borderColor: "rgba(255,255,255,0.15)",
          borderWidth: 1,
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          offset: true,
          ticks: {
            color: tickColor,
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: maxTicks,
            font: { size: 12 },
          },
        },
        y: {
          grid: { display: false },
          border: { display: false },
          beginAtZero: true,
          ticks: { color: tickColor, font: { size: 12 } },
        },
      },
    };

    return (
      <Box className="db-chart-panel">
        <Typography className="db-chart-title">
          {this.tr("dashboardPage.scheduleSummary") || "Schedule Summary"}
        </Typography>
        <Box className="db-chart-tabs">
          {[
            { key: "3Days", label: this.tr("dashboardPage.last3Days") },
            { key: "week", label: this.tr("dashboardPage.last7Days") },
            { key: "month", label: this.tr("dashboardPage.lastMonth") },
          ].map(({ key, label }) => (
            <span
              key={key}
              className={`db-tab${timeRange === key ? " active" : ""}`}
              onClick={() => this.setState({ timeRange: key })}>
              {label}
            </span>
          ))}
        </Box>

        <Line data={data} options={options} />

        <Box className="db-chart-legend">
          <Box className="db-legend-item">
            <span className="db-legend-dot" style={{ background: "#00E5FF" }} />
            <Typography className="db-legend-label">
              {this.tr("dashboardPage.completed")}
            </Typography>
          </Box>
          <Box className="db-legend-item">
            <span className="db-legend-dot" style={{ background: "#B6B4FF" }} />
            <Typography className="db-legend-label">
              {this.tr("dashboardPage.failed")}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Mini stat cards ───────────────────────────────────────────────────────

  renderMiniCards() {
    const { postTasks } = this.state;
    const weekTasks = filterByRange(postTasks, "week");
    const monthTasks = filterByRange(postTasks, "month");

    const weekSparkline = [0, 0, 0, 0, 0, 0, 0];
    weekTasks.forEach((t) => {
      if (t.status !== "fail")
        weekSparkline[new Date(t.schedule_utc_date).getDay()] += 1;
    });

    const socialTypeCounts = postTasks.reduce((acc, t) => {
      acc[t.social_type] = (acc[t.social_type] || 0) + 1;
      return acc;
    }, {});
    const monthSparkline = [
      "facebook", "linkedin", "instagram", "telegram",
      "reddit", "twitter", "tiktok", "youtube",
    ].map((s) => socialTypeCounts[s] || 0);

    const sparklineOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
      scales: {
        y: { display: false, beginAtZero: true },
        x: { display: false, grid: { display: false }, border: { display: false } },
      },
      elements: { point: { radius: 0 }, line: { tension: 0.5 } },
      interaction: { intersect: false },
    };

    const mkData = (values, color) => ({
      labels: values.map((_, i) => i),
      datasets: [{
        data: values,
        borderColor: color,
        borderWidth: 2.5,
        pointRadius: 0,
        fill: true,
        tension: 0.5,
        backgroundColor: (ctx) => {
          const { chartArea } = ctx.chart;
          if (!chartArea) return "transparent";
          const g = ctx.chart.ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
          g.addColorStop(0, color + "18");
          g.addColorStop(1, color + "55");
          return g;
        },
      }],
    });

    const weekCompleted = weekTasks.filter((t) => t.status !== "fail").length;
    const weekPct = weekTasks.length
      ? Math.min(100, (weekCompleted / weekTasks.length) * 100).toFixed(0)
      : 0;
    const monCompleted = monthTasks.filter((t) => t.status !== "fail").length;
    const monPct = monthTasks.length
      ? Math.min(100, (monCompleted / monthTasks.length) * 100).toFixed(0)
      : 0;

    return (
      <Box className="db-mini-cards-row">
        <MiniStatCard
          label="Total week posts"
          value={weekTasks.length}
          chartData={mkData(weekSparkline, "#00E5FF")}
          chartOptions={sparklineOptions}
          trend={`${weekPct}% completion`}
          trendColor="#84FEBC"
        />
        <MiniStatCard
          label="Total month posts"
          value={monthTasks.length}
          chartData={mkData(monthSparkline, "#B6B4FF")}
          chartOptions={sparklineOptions}
          trend={`${monPct}% completion`}
          trendColor="#B6B4FF"
        />
      </Box>
    );
  }

  // ── Account analytics (OP#3456) ───────────────────────────────────────────

  renderAccountAnalytics() {
    const { accounts, isLoading } = this.state;
    // Wait for the account list: an empty list while loading would flash "connect an account".
    if (isLoading) return null;

    return <AccountAnalytics variant="panel" accounts={accounts} />;
  }

  // ── Social type badges ────────────────────────────────────────────────────

  renderSocialBadges() {
    const { postTasks } = this.state;
    const counts = postTasks.reduce((acc, t) => {
      acc[t.social_type] = (acc[t.social_type] || 0) + 1;
      return acc;
    }, {});
    const social = [
      "instagram", "facebook", "linkedin", "telegram",
      "reddit", "twitter", "tiktok", "youtube",
    ];

    return (
      <Box className="db-social-scroll">
        {social.map((type) => (
          <Box key={type} className="db-social-badge">
            <IconsAccounts
              socialType={type}
              width="34px"
              height="34px"
              style={{ borderRadius: "50%" }}
            />
            <Typography className="db-social-count">{counts[type] || 0}</Typography>
            <Typography className="db-social-posts">
              {this.tr("dashboardPage.posts")}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  // ── Left column ───────────────────────────────────────────────────────────

  renderLeftColumn() {
    return (
      <Box className="db-col">
        {this.renderScheduleChart()}
        {this.renderMiniCards()}
        {this.renderAccountAnalytics()}
        {this.renderSocialBadges()}
      </Box>
    );
  }

  // ── Posts history ─────────────────────────────────────────────────────────

  renderPostsHistory() {
    const { postTasks, accounts, metaData } = this.state;
    const lastPosts = postTasks
      .filter((p) => p.status === "success" || p.status === "fail")
      .slice(-5)
      .reverse();

    return (
      <Box className="db-col">
        <Box className="db-col-header">
          <Typography className="db-col-title">Posts History</Typography>
          <Link to="/app/schedule" className="db-showall">
            SHOW ALL
          </Link>
        </Box>

        {lastPosts.length > 0 ? (
          lastPosts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              accounts={accounts}
              metaData={metaData}
              tr={(k) => this.tr(k)}
            />
          ))
        ) : (
          <Box className="db-empty">
            <NoPost style={{ width: "60%", maxWidth: 200 }} />
            <Typography className="db-empty-title">
              {this.tr("dashboardPage.empty")}
            </Typography>
            <Typography className="db-empty-sub">
              {this.tr("dashboardPage.lookEmpty")}
            </Typography>
            <Link
              to="/app/post"
              className="btnDark mt-3"
              style={{ borderRadius: 23 }}>
              {this.tr("dashboardPage.btnAdd")}
            </Link>
          </Box>
        )}
      </Box>
    );
  }

  // ── Upcoming posts ────────────────────────────────────────────────────────

  renderUpcomingPosts() {
    const { postTasks, posts, accounts, metaData } = this.state;
    const now = new Date();
    const upcoming = postTasks
      .filter(
        (p) => p.status === "queued" && new Date(p.schedule_utc_date) > now
      )
      .slice(0, 5);

    return (
      <Box className="db-col">
        <Box className="db-col-header">
          <Typography className="db-col-title">Upcoming Posts</Typography>
          <Link to="/app/schedule" className="db-showall">
            SHOW ALL
          </Link>
        </Box>

        {upcoming.length > 0 ? (
          upcoming.map((post) => (
            <UpcomingCard
              key={post.id}
              post={post}
              posts={posts}
              accounts={accounts}
              metaData={metaData}
            />
          ))
        ) : (
          <Box className="db-empty">
            <NoData style={{ width: "60%", maxWidth: 200 }} />
            <Typography className="db-empty-title">
              {this.tr("dashboardPage.empty")}
            </Typography>
            <Typography className="db-empty-sub">
              {this.tr("dashboardPage.upLookEmpty")}
            </Typography>
            <Link
              to="/app/post"
              className="btnDark mt-3"
              style={{ borderRadius: 23 }}>
              {this.tr("dashboardPage.schBtnAdd")}
            </Link>
          </Box>
        )}
      </Box>
    );
  }

  // ── Root ──────────────────────────────────────────────────────────────────

  render() {
    return (
      <Box className="dashboardPage">
        <Box className="db-topbar">
          <Typography className="db-page-title">
            {this.tr("pages.dashboard")}
          </Typography>
        </Box>
        <Box className="db-grid">
          {this.renderLeftColumn()}
          {this.renderPostsHistory()}
          {this.renderUpcomingPosts()}
        </Box>
      </Box>
    );
  }
}

export default function Dashboard() {
  return <DashboardPage {...useOutletContext()} />;
}
